import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { StatutContainer } from '@cockpit/shared';
import { api } from '../lib/api';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { formatDate, formatEuro } from '../lib/format';

const BADGE_BY_STATUT: Record<StatutContainer, string> = {
  Commande: 'bg-slate-700 text-slate-200',
  En_transit: 'bg-blue-700 text-blue-100',
  Receptionne: 'bg-emerald-700 text-emerald-100',
};

const LABELS: Record<StatutContainer, string> = {
  Commande: 'Commandé',
  En_transit: 'En transit',
  Receptionne: 'Réceptionné',
};

function fmtM2(c: number): string {
  return `${(c / 100).toFixed(2).replace('.', ',')} m²`;
}

export function ContainersPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const statut = searchParams.get('statut') ?? '';

  const query = useQuery({
    queryKey: ['containers', statut],
    queryFn: () =>
      api.containers.list({ statut: statut === '' ? undefined : statut, page_size: 100 }),
  });

  return (
    <div className="max-w-7xl">
      <header className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Containers</h1>
          <p className="text-xs text-slate-500 mt-1">Importations Vietnam → France.</p>
        </div>
        <Link to="/containers/nouveau">
          <Button>+ Nouveau container</Button>
        </Link>
      </header>

      <div className="mb-4 flex gap-3 items-center">
        <select
          value={statut}
          onChange={(e): void => {
            const sp = new URLSearchParams(searchParams);
            if (e.target.value === '') sp.delete('statut');
            else sp.set('statut', e.target.value);
            setSearchParams(sp, { replace: true });
          }}
          className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
        >
          <option value="">Tous statuts</option>
          <option value="Commande">Commandé</option>
          <option value="En_transit">En transit</option>
          <option value="Receptionne">Réceptionné</option>
        </select>
      </div>

      {query.isLoading && <div className="text-sm text-slate-400">Chargement…</div>}
      {query.isSuccess && query.data.items.length === 0 && (
        <EmptyState
          title="Aucun container."
          description="Créez votre premier container d'importation."
          action={
            <Link to="/containers/nouveau">
              <Button>+ Nouveau container</Button>
            </Link>
          }
        />
      )}
      {query.isSuccess && query.data.items.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {query.data.items.map((c) => (
            <Link
              key={c.id}
              to={`/containers/${String(c.id)}`}
              className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 hover:border-floryon-green/50 transition-colors"
            >
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-lg font-semibold text-slate-100">{c.reference_container}</div>
                <span className={`px-2 py-0.5 rounded text-xs ${BADGE_BY_STATUT[c.statut]}`}>
                  {LABELS[c.statut]}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="text-slate-500">Commande</div>
                  <div className="text-slate-300">{formatDate(c.date_commande)}</div>
                </div>
                <div>
                  <div className="text-slate-500">Arrivée prévue</div>
                  <div className="text-slate-300">{formatDate(c.date_arrivee_prevue)}</div>
                </div>
                <div>
                  <div className="text-slate-500">Rouleaux prévus</div>
                  <div className="text-slate-300">{c.nb_rouleaux_total}</div>
                </div>
                <div>
                  <div className="text-slate-500">m² prévus</div>
                  <div className="text-slate-300">{fmtM2(c.m2_total_centi)}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-slate-500">Coût total (HT)</div>
                  <div className="text-floryon-green font-semibold">
                    {formatEuro(c.cout_total_eur_centimes)}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
